import React, { useEffect, useState } from "react";
import { Mail, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { authApi, errorMessage } from "../../lib/api";

const ROWS = [
  { key: "confirmed", label: "Order & ride confirmed", hint: "When a driver accepts or your order is placed/confirmed." },
  { key: "cancelled", label: "Cancelled or rejected", hint: "When a ride or order is cancelled or declined." },
  { key: "completed", label: "Completed / delivered", hint: "When your ride finishes." },
  { key: "invoice", label: "Invoices & receipts", hint: "Your trip and order invoice emails." },
];

const DEFAULTS = ROWS.reduce((a, r) => ({ ...a, [r.key]: { email: true, whatsapp: true } }), {});

// Per-event email + WhatsApp toggles. Default on; turning off makes that event app/push-only.
// SOS and refund alerts always send and aren't shown here.
export default function NotificationPrefs({ user, onSaved }) {
  const [prefs, setPrefs] = useState(() => ({ ...DEFAULTS, ...(user?.notify_prefs || {}) }));
  const [busy, setBusy] = useState(null); // `${key}-${channel}` currently saving
  const waMaster = user?.whatsapp_opt_in === true;

  useEffect(() => { setPrefs({ ...DEFAULTS, ...(user?.notify_prefs || {}) }); }, [user?.notify_prefs]);

  const toggle = async (key, channel, value) => {
    const cell = `${key}-${channel}`;
    setBusy(cell);
    const next = { ...prefs, [key]: { ...prefs[key], [channel]: value } };
    setPrefs(next);
    try {
      const u = await authApi.updateProfile({ notify_prefs: { [key]: { [channel]: value } } });
      onSaved?.(u.user || u);
    } catch (e) {
      setPrefs(prefs); // revert
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-1" data-testid="notification-prefs">
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 border-b border-surface-200 pb-2 text-xs font-semibold text-ink-muted">
        <span>Event</span>
        <span className="flex items-center gap-1 justify-self-center"><Mail className="h-3.5 w-3.5" /> Email</span>
        <span className="flex items-center gap-1 justify-self-center"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</span>
      </div>
      {ROWS.map((r) => {
        const p = prefs[r.key] || DEFAULTS[r.key];
        return (
          <div key={r.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-6 py-2.5" data-testid={`notify-row-${r.key}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink">{r.label}</p>
              <p className="text-xs text-ink-muted">{r.hint}</p>
            </div>
            <div className="justify-self-center">
              {busy === `${r.key}-email` ? <Loader2 className="h-4 w-4 animate-spin text-ink-muted" /> :
                <Switch checked={p.email !== false} onCheckedChange={(v) => toggle(r.key, "email", v)} disabled={busy !== null} data-testid={`notify-${r.key}-email`} />}
            </div>
            <div className="justify-self-center">
              {busy === `${r.key}-whatsapp` ? <Loader2 className="h-4 w-4 animate-spin text-ink-muted" /> :
                <Switch checked={p.whatsapp !== false && waMaster} onCheckedChange={(v) => toggle(r.key, "whatsapp", v)} disabled={busy !== null || !waMaster} data-testid={`notify-${r.key}-whatsapp`} />}
            </div>
          </div>
        );
      })}
      {!waMaster && <p className="pt-1 text-xs text-ink-muted" data-testid="notify-wa-master-note">Turn on <span className="font-medium">WhatsApp updates</span> above to control WhatsApp per event.</p>}
      <p className="pt-2 text-xs text-ink-muted">You'll always see every update in the app. SOS and refund alerts are always sent.</p>
    </div>
  );
}
